import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import { Grid, Box } from "@mui/material";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import { CARBON_CREDIT_OFFSET_MANAGEMENT_FORM } from "data/forms/griFormSchema";
import {
  trackVerifyOffsetProj,
  carbonOffsetCreditMang,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import { OFFSET_STANDARD_SELECTOR } from "data/standards/griStandards";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";

const CarbonOffsetCreditMang = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  // Force re-render of components when step changes
  const [forceUpdate, setForceUpdate] = useState(0);

  const steps = [
    "Emissions Tracking Setup",
    "Methodology Selection",
    "Organization Information",
    "Upload Evidence & Data",
    "Real-time Emissions Calculation",
    "Upload Evidence and Review Reports",
    "Download Gap Assessment",
    "Upload Gap Form",
    "Final Reports",
  ];

  useEffect(() => {
    if (activeStep === 5 && prevStep === 4) {
      setShowReviewPopup(true);
    }
    setForceUpdate((prev) => prev + 1);
  }, [activeStep, prevStep]);

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(6);
    setShowReviewPopup(false);
  };

  const DEFAULT_EVIDENCE_TYPES = [
    "Emissions / Offset Project Details",
    "Total emissions by scope (linked from Emissions Module)",
    "Planned offset type (e.g., reforestation, biochar, renewable energy)",
    "Geographic location and scale",
    "Supporting documents (methodology, baselines, MRV)",
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            key="emissions-tracking-step"
            onPrevious={handleBack}
            onNext={handleNext}
            ReportDownloadButtonShow={false}
            initialItems={carbonOffsetCreditMang}
            title="Carbon Offset & Credit Management"
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );

      case 1:
        return (
          <StandardsSelector
            standards={OFFSET_STANDARD_SELECTOR}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select Credit Type or Standard"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 2:
        return (
          <ReusableFormComponent
            {...CARBON_CREDIT_OFFSET_MANAGEMENT_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 3:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
            title="Upload GHG Data & Evidence"
            description="Upload your emissions data, energy consumption logs, and supporting documentation"
            evidenceTypes={DEFAULT_EVIDENCE_TYPES}
          />
        );
      case 4:
        return (
          <DownloadReport
            key="realtime-emissionsCalculation-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={trackVerifyOffsetProj}
            title=""
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );
      case 5:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 6:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
            title="Gap Assessment Form"
            description="Download gap assessment form to identify missing data or improvement areas"
          />
        );
      case 7:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
            title="Upload Completed Gap Assessment"
            description="Upload your completed gap assessment for final analysis"
          />
        );
      case 8:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            showReportCountdownButton={true}
            title="Final GHG Reports"
            description="Download your complete GHG emissions reports and regulatory submissions"
            standardName="Carbon Reports"
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader
              title="AI-Driven Dashboard for Carbon Credit Assessment, Offset Management, and Emissions Trading Compliance. "
              subtitle="AI-Driven Dashboard for Carbon Credit Assessment, Offset Management, and Emissions Trading Compliance. "
            />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg2_4"
                title="Carbon Management"
                activeStep={activeStep}
                onBack={handleBack}
                steps={steps}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box key={`step-content-${activeStep}-${forceUpdate}`}>
            {getStepContent(activeStep)}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          >
            {/* <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                                Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                            </Box>
                        </Box> */}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default CarbonOffsetCreditMang;
